import { useEffect, useRef } from 'react';
import { getSelectedArray } from './';

/**
 * useMenuIconTitlesEffect
 * Sets the title attribute for Kendo column menu icons for accessibility.
 * @param {Object} processedData - The processed data for the table.
 * @return {void}
 */
const useMenuIconTitlesEffect = (gridRef, processedData) => {
  useEffect(() => {
    if (!gridRef?.current) return;
    const menuIcons = gridRef.current.querySelectorAll('.k-grid-header-menu.k-grid-column-menu');
    menuIcons.forEach(icon => {
      icon.setAttribute('title', 'Open column menu');
    });
  }, [gridRef, processedData]);
};

/**
 * useTableHeaderTitlesEffect
 * Sets the title attribute for Kendo table header cells for accessibility.
 * @param {Object} gridRef - Ref to the Kendo grid DOM node.
 * @param {Array|Object} processedData - The processed data for the table.
 * @return {void}
 */
const useTableHeaderTitlesEffect = (gridRef, processedData) => {
  useEffect(() => {
    if (!gridRef?.current) return;
    const headerCells = gridRef.current.querySelectorAll('.k-grid-header th');
    headerCells.forEach(cell => {
      if (cell.textContent) {
        cell.setAttribute('title', cell.textContent.trim());
      }
    });
  }, [gridRef, processedData]);
};
  
/**
 * useFilterInputPlaceholderEffect
 * Sets the placeholder for Kendo filter inputs.
 * Runs only once on mount.
 */
const useFilterInputPlaceholderEffect = () => {
  useEffect(() => {
    let filterInputs = document.querySelectorAll(
      '.k-filtercell-wrapper .k-textbox .k-input-inner'
    );
    if (filterInputs.length > 0) {
      filterInputs.forEach((el) => {
        el.setAttribute('placeholder', 'Search...');
      });
    }
  }, []);
};

/**
 * useDefaultSelectEffect
 * Syncs the select state with defaultSelect prop changes.
 * Ensures table is set to default row selection after first render.
 * Used for checkbox selectable rows in a selectable table.
 * @param {Object} defaultSelect - The default selection state that selects rows at table render.
 * @param {boolean} selectable - Whether row selection is enabled.
 * @param {Object} select - The current state of which rows are selected.
 * @param {Function} setSelect - Function to update the current selection state.
 */
const useDefaultSelectEffect = (selectable, defaultSelect, select, setSelect) => {
  const prevDefaultSelect = useRef(defaultSelect);

  useEffect(() => {
    if (
      selectable &&
      JSON.stringify(defaultSelect) !== JSON.stringify(prevDefaultSelect.current)
    ) {
      setSelect(defaultSelect);
    } else if (!selectable && Object.keys(select).length > 0) {
      setSelect({});
    }
    prevDefaultSelect.current = defaultSelect;
  }, [defaultSelect, selectable]);
};

/**
 * useSaveSelectionEffect
 * Calls handleSaveSelection when select changes, if not using a save button.
 * @param {boolean} selectable - Whether row selection is enabled.
 * @param {Object} select - The current selection state.
 * @param {Function} handleSaveSelection - Function to call when selection changes.
 * @param {Array} data - The data array for the table.
 * @param {string} dataItemKey - The unique data item key for selection.
 * @param {boolean} showSaveSelectionButton - Whether to use a Save Selection button.
 * @param {Object} defaultSelect - The default selection state for comparison.
 */
const useSaveSelectionEffect = ({
  selectable,
  select,
  handleSaveSelection,
  data,
  dataItemKey,
  showSaveSelectionButton,
  defaultSelect
}) => {
  useEffect(() => {
    if (
      selectable &&
      typeof handleSaveSelection === 'function' &&
      !showSaveSelectionButton &&
      JSON.stringify(select) !== JSON.stringify(defaultSelect) // Only run if selection is different
    ) {
      const selectedArray = getSelectedArray(data, select, dataItemKey);
      handleSaveSelection(selectedArray);
    }
  }, [select, selectable, showSaveSelectionButton]);
};

export {
  useMenuIconTitlesEffect,
  useFilterInputPlaceholderEffect,
  useTableHeaderTitlesEffect,
  useDefaultSelectEffect,
  useSaveSelectionEffect
};